import { Lock01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { NativeSelect } from '@/lib/base/forms/select/select-native';
import { cx } from '@/utils/cx';

import type { UpdateMappingBody } from '../api/fieldMappings';
import type { AhoyPropertyOption, SalesforceFieldMapping } from '../types';

export type MappingUpdate = UpdateMappingBody;

interface EditableMappingRowProps {
  mapping: SalesforceFieldMapping;
  availableAhoyProperties: AhoyPropertyOption[];
  isSaving: boolean;
  onUpdate: (mappingId: number, body: MappingUpdate) => void;
}

const NOT_MAPPED_VALUE = '';

// Shared grid template so the column-header row lines up with each row's controls.
// Three columns: field, Ahoy property, direction (read-only label in v1).
export const MAPPING_ROW_GRID =
  'sm:grid sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.8fr)] sm:items-center sm:gap-3';

const MOBILE_LABEL_CLASS =
  'mb-1 block text-xs font-medium text-[var(--color-text-tertiary)] sm:hidden';

// Compact select. `min-w-0` lets the <select> shrink below its longest option's
// intrinsic width; `pr-10` keeps the value text clear of the chevron.
const SELECT_CLASS = 'min-w-0 py-1.5 text-sm pr-10';

export function EditableMappingRow({
  mapping,
  availableAhoyProperties,
  isSaving,
  onUpdate,
}: EditableMappingRowProps) {
  const isLocked = mapping.is_required_in_salesforce;
  const isMapped = !!mapping.ahoy_property;

  const propertyOptions = useMemo(
    () => [
      { value: NOT_MAPPED_VALUE, label: "Don't sync" },
      ...availableAhoyProperties.map(p => ({ value: p.name, label: p.label })),
    ],
    [availableAhoyProperties]
  );

  const handlePropertyChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target;
      if (!value) {
        onUpdate(mapping.id, {
          ahoy_property: null,
          sync_direction: 'skipped',
        });
        return;
      }
      if (!isMapped) {
        onUpdate(mapping.id, {
          ahoy_property: value,
          sync_direction: 'external_to_ahoy',
        });
        return;
      }
      onUpdate(mapping.id, { ahoy_property: value });
    },
    [mapping.id, isMapped, onUpdate]
  );

  const dotColor = isMapped
    ? 'bg-[var(--color-fg-success-primary)]'
    : 'bg-[var(--color-fg-quaternary)]';

  return (
    <div className={cx('px-4 py-2.5', MAPPING_ROW_GRID)}>
      {/* Salesforce field identity */}
      <div className='flex min-w-0 items-center gap-2.5'>
        <span
          className={cx('h-1.5 w-1.5 shrink-0 rounded-full', dotColor)}
          aria-hidden='true'
        />
        <span className='truncate text-sm font-medium text-[var(--color-text-primary)]'>
          {mapping.salesforce_field}
        </span>
        <span className='truncate text-xs text-[var(--color-text-tertiary)]'>
          {mapping.salesforce_field_type}
          {mapping.is_required_in_salesforce && (
            <span className='text-[var(--color-text-warning-primary)]'>
              {' · required'}
            </span>
          )}
        </span>
        {isLocked && (
          <Lock01
            className='h-3.5 w-3.5 shrink-0 text-[var(--color-text-tertiary)]'
            aria-label='Locked'
          />
        )}
      </div>

      {/* Ahoy property (also the on/off control via "Don't sync") */}
      <div className='mt-2 min-w-0 sm:mt-0'>
        <span className={MOBILE_LABEL_CLASS}>Ahoy property</span>
        <NativeSelect
          aria-label={`Ahoy property for ${mapping.salesforce_field}`}
          options={propertyOptions}
          value={mapping.ahoy_property ?? NOT_MAPPED_VALUE}
          onChange={handlePropertyChange}
          disabled={isLocked || isSaving}
          selectClassName={SELECT_CLASS}
        />
      </div>

      {/* Direction — read-only label in v1 (one-way inbound only) */}
      <div className='mt-2 min-w-0 sm:mt-0'>
        <span className={MOBILE_LABEL_CLASS}>Direction</span>
        <p className='text-sm text-[var(--color-text-secondary)]'>
          {isMapped ? 'Salesforce → Ahoy' : 'Not syncing'}
        </p>
      </div>
    </div>
  );
}
